import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { fmt } from "@/data/packs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderParam = params.get("order_number");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error | expired
  const [amount, setAmount] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const { clearAll } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId && !orderParam) {
      setStatus("error");
      return;
    }
    let polls = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = sessionId
          ? await axios.get(`${API}/payments/status/${sessionId}`)
          : await axios.get(`${API}/orders/track/${orderParam}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setOrderNumber(data.order_number);
          setStatus("paid");
          if (!cleared.current) {
            clearAll();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        // keep trying
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        setStatus("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId, orderParam, clearAll]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-white rounded-[1.75rem] border border-[var(--oz-border)] p-8 md:p-10 text-center shadow-[0_30px_60px_-30px_rgba(26,23,20,0.4)]">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-14 h-14 mx-auto text-[var(--oz-gold)] animate-spin" />
            <h1 className="font-serif text-2xl mt-6 text-[var(--oz-text)]">Confirmation en cours…</h1>
            <p className="mt-2 text-sm text-[var(--oz-muted)]">Nous vérifions votre paiement, merci de patienter.</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-[var(--oz-nude)] flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-[var(--oz-badge)]" />
            </div>
            <h1 className="font-serif text-3xl mt-6 text-[var(--oz-text)]">Merci pour votre commande !</h1>
            <p className="mt-3 text-sm text-[var(--oz-muted)] leading-relaxed">
              Votre paiement {amount ? `de ${fmt(amount)} ` : ""}a bien été confirmé. Vous recevrez
              votre sélection de produits Ozentya. Prêt(e) à les présenter et à développer votre
              activité à votre rythme.
            </p>
            {orderNumber && (
              <div className="mt-5 bg-[var(--oz-bg-2)] rounded-2xl px-5 py-4">
                <div className="text-xs uppercase tracking-wider text-[var(--oz-muted)]">Numéro de commande</div>
                <div data-testid="success-order-number" className="font-serif text-2xl text-[var(--oz-text)]">{orderNumber}</div>
                <p className="text-xs text-[var(--oz-muted)] mt-1">
                  Un email de confirmation vous a été envoyé. Conservez ce numéro pour suivre votre commande.
                </p>
              </div>
            )}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                data-testid="success-home-link"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors"
              >
                Retour à l'accueil <ArrowRight className="w-4 h-4" />
              </Link>
              {orderNumber && (
                <Link
                  to={`/suivi?order=${orderNumber}`}
                  data-testid="success-track-link"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-full border border-[var(--oz-dark)] font-semibold text-sm hover:bg-[var(--oz-bg-2)] transition-colors"
                >
                  Suivre ma commande
                </Link>
              )}
            </div>
          </div>
        )}

        {status === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 className="w-14 h-14 mx-auto text-[var(--oz-gold)]" />
            <h1 className="font-serif text-2xl mt-6 text-[var(--oz-text)]">Paiement en traitement</h1>
            <p className="mt-2 text-sm text-[var(--oz-muted)]">
              Votre paiement est en cours de traitement. Vous recevrez une confirmation sous peu.
            </p>
            <Link to="/" className="mt-6 inline-block text-sm font-semibold text-[var(--oz-gold)]">Retour à l'accueil</Link>
          </div>
        )}

        {(status === "error" || status === "expired") && (
          <div data-testid="payment-error">
            <XCircle className="w-14 h-14 mx-auto text-[var(--oz-badge)]" />
            <h1 className="font-serif text-2xl mt-6 text-[var(--oz-text)]">Un souci est survenu</h1>
            <p className="mt-2 text-sm text-[var(--oz-muted)]">
              Nous n'avons pas pu confirmer ce paiement. Vous pouvez réessayer depuis votre panier.
            </p>
            <Link to="/" className="mt-6 inline-block text-sm font-semibold text-[var(--oz-gold)]">Retour à l'accueil</Link>
          </div>
        )}
      </div>
    </div>
  );
}
